import { combineReducers, configureStore } from "@reduxjs/toolkit";
import authReducer from "./auth/authSlice";
import { apiSlice } from "./apiSlice";
import searchReducer from "./search/searchSlice";
import companyReducer from "./Company/companySlice";
import enterpriseReducer from "./enterprise/enterpriseSlice";
import claimsReducer from "./claim/claimSlice";
import activeTabReducer from "./activeTabSlice";
import activeCompanyReducer from "./activeCompanySlice";
// TIP-614 — the brief price, refreshed by every allowance fetch.
import briefPricingReducer from "./briefs/briefPricingSlice";
import handle401Middleware from "../middleware/handle401Middleware";

// Function to load state from local storage

/* Strip anything token-shaped from a persisted `auth` slice. */
const withoutCredentials = (auth) => {
  if (!auth) return auth;
  const { token, refresh, access, ...safe } = auth;
  return safe;
};

const loadState = () => {
  try {
    const serializedState = localStorage.getItem('state');
    if (serializedState === null) {
      return undefined;
    }
    const state = JSON.parse(serializedState);
    return {
      claims: state.claims,
      auth: withoutCredentials(state.auth),
      company: state.company,
      enterprise: state.enterprise,
      activeTab: state.activeTab,
    };
  } catch (err) {
    return undefined;
  }
};

// Function to save state to local storage
const saveState = (state) => {
  try {
    const stateToPersist = {
      claims: state.claims,
      auth: withoutCredentials(state.auth),
      company: state.company,
      enterprise: state.enterprise,
      activeTab: state.activeTab,
    };
    const serializedState = JSON.stringify(stateToPersist);
    localStorage.setItem('state', serializedState);
  } catch (err) {
  }
};
const persistedState = loadState();

const appReducer = combineReducers({
  auth: authReducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  search: searchReducer,
  company: companyReducer,
  enterprise: enterpriseReducer,
  claims: claimsReducer,
  activeTab: activeTabReducer,
  activeCompany: activeCompanyReducer,
  // TIP-614 — the brief price as the allowance endpoint last reported it.
  briefPricing: briefPricingReducer,
});

/* Drop the RTK Query cache when the session ends. */
const SESSION_END_ACTIONS = ["auth/logout", "auth/clearUserInfo", "auth/clearIdentityScopedState"];

const rootReducer = (state, action) => {
  if (state && SESSION_END_ACTIONS.includes(action.type)) {
    return appReducer({ ...state, [apiSlice.reducerPath]: undefined }, action);
  }
  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  preloadedState: persistedState,
  middleware: (getDefaultMiddleware) =>
    // handle401Middleware was imported but never registered, so the
    // "Logged in on another device" 401 never raised its modal.
    getDefaultMiddleware().concat(apiSlice.middleware, handle401Middleware),
  devTools: process.env.NODE_ENV !== "production",
});

// Save state to local storage on page unload
window.addEventListener('beforeunload', () => {
  saveState(store.getState());
});

// Clear the state from local storage after rehydrating
if (persistedState) {
  localStorage.removeItem('state');
}

export default store;
